import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Calendar as CalendarIcon, Clock, FileText, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const localizer = momentLocalizer(moment);

const ScheduledPosts = () => {
  const { getAuthHeaders } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar');

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/scheduled-posts`, { headers: getAuthHeaders() });
      setPosts(response.data);
    } catch (err) {
      console.error('Failed to fetch scheduled posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const calendarEvents = useMemo(() =>
    posts.map(post => ({
      title: post.article_title || post.title || 'Scheduled Post',
      start: new Date(post.scheduled_at),
      end: new Date(post.scheduled_at),
      resource: post
    })), [posts]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  return (
    <div className="fade-in" data-testid="scheduled-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Scheduled Posts</h1>
          <p className="text-sm text-[var(--color-muted)]">
            {posts.length} post{posts.length !== 1 ? 's' : ''} scheduled
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView('calendar')}
            className={`btn text-sm ${view === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}>
            <CalendarIcon className="w-3.5 h-3.5" /> Calendar
          </button>
          <button onClick={() => setView('list')}
            className={`btn text-sm ${view === 'list' ? 'btn-primary' : 'btn-secondary'}`}>
            <FileText className="w-3.5 h-3.5" /> List
          </button>
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="card p-14 text-center">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--color-surface)]">
            <CalendarIcon className="w-7 h-7 text-[var(--color-coral)]" />
          </div>
          <h3 className="font-display text-base font-medium mb-2">No scheduled posts</h3>
          <p className="text-sm text-[var(--color-muted)]">
            Schedule articles for publishing from the article editor.
          </p>
        </div>
      ) : view === 'calendar' ? (
        <div className="card p-6 stilos-calendar">
          <style>{`
            .stilos-calendar .rbc-toolbar { font-family: 'Inter', sans-serif; margin-bottom: 1.5rem; }
            .stilos-calendar .rbc-toolbar button { border: 1px solid var(--color-border); border-radius: 9999px; padding: 0.375rem 1rem; font-size: 0.8125rem; background: transparent; cursor: pointer; transition: all 0.15s; }
            .stilos-calendar .rbc-toolbar button:hover { background: var(--color-surface); }
            .stilos-calendar .rbc-toolbar button.rbc-active { background: var(--color-charcoal); color: white; border-color: var(--color-charcoal); }
            .stilos-calendar .rbc-header { font-size: 0.75rem; font-weight: 500; padding: 0.5rem; color: var(--color-muted); border-bottom: 1px solid var(--color-border); }
            .stilos-calendar .rbc-month-view { border: 1px solid var(--color-border); border-radius: 12px; overflow: hidden; }
            .stilos-calendar .rbc-day-bg + .rbc-day-bg, .stilos-calendar .rbc-month-row + .rbc-month-row { border-color: var(--color-border); }
            .stilos-calendar .rbc-today { background: rgba(232, 115, 92, 0.04); }
            .stilos-calendar .rbc-event { background: var(--color-coral); border-radius: 6px; padding: 2px 6px; font-size: 0.6875rem; border: none; }
            .stilos-calendar .rbc-off-range-bg { background: var(--color-surface); }
            .stilos-calendar .rbc-date-cell { font-size: 0.75rem; padding: 0.25rem 0.5rem; }
          `}</style>
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 550 }}
            views={['month', 'week']}
            defaultView="month"
            components={{
              toolbar: CustomToolbar
            }}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div key={post.id} className="article-card" data-testid={`post-${post.id}`}>
              <div className="article-card-content">
                <h3 className="article-title">{post.article_title || post.title || 'Scheduled Post'}</h3>
                <div className="article-meta">
                  <span className={`badge badge-${post.status}`}>{post.status}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(post.scheduled_at).toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    {post.platform}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CustomToolbar = ({ label, onNavigate }) => (
  <div className="flex items-center justify-between mb-4">
    <button onClick={() => onNavigate('PREV')} className="btn btn-ghost p-2">
      <ChevronLeft className="w-4 h-4" />
    </button>
    <span className="font-display text-base font-medium">{label}</span>
    <button onClick={() => onNavigate('NEXT')} className="btn btn-ghost p-2">
      <ChevronRight className="w-4 h-4" />
    </button>
  </div>
);

export default ScheduledPosts;
